import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// A missing/incomplete .env silently breaks token generation, so surface it.
const MISSING_CONFIG = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (MISSING_CONFIG.length) {
  console.warn(
    "Firebase config is incomplete - push notifications are disabled. " +
    "Missing REACT_APP_FIREBASE_* env values for: " + MISSING_CONFIG.join(", ")
  );
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Cloud Messaging and get a reference to the service
let messaging = null;
if (typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window) {
  try {
    messaging = getMessaging(app);
  } catch (error) {
    console.warn("Firebase Messaging initialization failed:", error);
  }
}

export const requestForToken = async () => {
  try {
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notification');
      return null;
    }
    if (!('serviceWorker' in navigator)) {
      console.log('Service Worker is not supported in this browser (or not over HTTPS).');
      return null;
    }
    if (!messaging) {
      console.log('Firebase Messaging is not initialized');
      return null;
    }
    if (!window.isSecureContext) {
      console.warn('Not a secure context (needs HTTPS or localhost) - cannot generate an FCM token.');
      return null;
    }
    if (!firebaseConfig.messagingSenderId || !firebaseConfig.apiKey) {
      console.warn('Missing Firebase config - cannot generate an FCM token. Check your .env file.');
      return null;
    }
    if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) {
      console.warn('Missing REACT_APP_FIREBASE_VAPID_KEY - cannot generate an FCM token.');
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission not granted.');
      return null;
    }

    // Register and WAIT for the service worker to be fully active
    const swUrl = `/firebase-messaging-sw.js?apiKey=${firebaseConfig.apiKey}&authDomain=${firebaseConfig.authDomain}&projectId=${firebaseConfig.projectId}&storageBucket=${firebaseConfig.storageBucket}&messagingSenderId=${firebaseConfig.messagingSenderId}&appId=${firebaseConfig.appId}&measurementId=${firebaseConfig.measurementId}`;
    const expectedUrl = new URL(swUrl, window.location.origin).href;

    // Drop any stale messaging worker registered with different (e.g. "undefined")
    // config params - its firebase.initializeApp() threw, so it can never deliver
    // background messages.
    const existing = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      existing
        .filter((reg) => {
          const scriptUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL;
          return scriptUrl
            && scriptUrl.includes('firebase-messaging-sw.js')
            && scriptUrl !== expectedUrl;
        })
        .map((reg) => {
          console.log('Unregistering stale messaging service worker:', reg.active?.scriptURL);
          return reg.unregister();
        })
    );

    const registration = await navigator.serviceWorker.register(swUrl);
    await navigator.serviceWorker.ready;

    const currentToken = await getToken(messaging, { 
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration
    });

    if (currentToken) {
      console.log('FCM Token generated successfully:', currentToken);
      return currentToken;
    } else {
      console.log('No registration token available. Request permission to generate one.');
      return null;
    }
  } catch (err) {
    console.error('An error occurred while retrieving token: ', err);
    return null;
  }
};

export const onMessageListener = () =>
  new Promise((resolve) => {
    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });

export { messaging };
